import { useEffect, useMemo, useRef, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UserPlus, FileSpreadsheet, Camera, Contact2, Send, Phone, MessageCircle, RefreshCw, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { LoanIcon, findLoan, loanRangeLabel, clampAmount } from "@/lib/loanUtils";

const SHEETJS_URL = "https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js";
const TESSERACT_URL = "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";

const SOURCE_TYPES = [
  "Direct", "Customer Referral", "Mahalir Kuzhu", "Bank Staff", "Field Staff",
  "Telecaller", "Agent", "My Phone Contacts", "Bulk Upload",
  "Collection Photo (OCR)", "Self Customer", "WhatsApp",
];

const roleLabel = (role, sub) => {
  if (role === "admin") return "Admin";
  if (role === "bank_staff") return "Bank Staff";
  if (role === "customer") return "Customer";
  if (role === "office_staff") {
    if (sub === "telecaller") return "Telecaller";
    if (sub === "field") return "Field Staff";
    if (sub === "district_manager") return "Mahalir Kuzhu Manager";
    return "Office Staff";
  }
  return role || "";
};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

const normalisePhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  return digits.slice(-10);
};
const validPhone = (p) => /^[6-9]\d{9}$/.test(p);

export default function UniversalLeadAdd() {
  const { user } = useAuth();
  const [openKey, setOpenKey] = useState(null);
  const [wa, setWa] = useState(null);

  const isCustomer = user?.role === "customer";
  const defaultSource = useMemo(() => {
    const lbl = roleLabel(user?.role, user?.sub_role);
    const district = user?.district || user?.managed_district || "";
    return `${lbl} - ${user?.name || user?.phone || ""}${district ? " - " + district : ""}`;
  }, [user]);

  const loadWa = () => {
    if (isCustomer) return;
    api.get("/whatsapp/today").then(r => setWa(r.data)).catch(() => {});
  };
  useEffect(() => { loadWa(); }, [user]);

  const [mainOpen, setMainOpen] = useState(false);
  const closeMain = () => { setMainOpen(false); setOpenKey(null); };

  return (
    <div data-testid="universal-lead-add" className="flex justify-end mb-4">
      <Button
        data-testid="add-customer-btn"
        onClick={() => setMainOpen(true)}
        className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full px-4 sm:px-6 h-10 shadow-lg shadow-amber-500/20"
      >
        <UserPlus className="w-4 h-4 mr-2"/>
        {isCustomer ? "Refer a Friend" : "Add Customer"}
      </Button>

      <Dialog open={mainOpen} onOpenChange={v => !v && closeMain()}>
        <DialogContent
          data-testid="add-customer-modal"
          className="bg-slate-900 border-amber-500/30 text-white max-w-3xl max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-lg md:text-xl text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-500"/>
              {isCustomer ? "Friend Number Add Pannu — Referral Bonus!" : "Customer Add Pannu"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Add a new customer lead via manual entry, bulk Excel upload, photo OCR, or phone contacts.
            </DialogDescription>
            <div className="text-xs md:text-sm text-slate-400 mt-1">
              {isCustomer
                ? "Ungal friends aur family la yaarukku loan venum na ipdi number kuduthiraga → nangal call panniduvom."
                : "Enge irunthum add pannunga — Manual, Excel, Photo, Contacts. Phone number mattum pothum!"}
            </div>
            {!isCustomer && wa && (
              <div className="mt-3 inline-flex items-center bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1.5 text-xs self-start" data-testid="wa-daily-chip">
                <MessageCircle className="w-3.5 h-3.5 mr-1 text-emerald-400"/>
                <span className="text-emerald-300 font-bold">{wa.count}/{wa.limit}</span>
                <span className="text-slate-400 ml-1"> WhatsApp Today</span>
              </div>
            )}
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <BigButton icon={UserPlus} label="Manual Add" tamil="Number Add Pannu" onClick={() => setOpenKey("manual")} testId="btn-manual-add"/>
            <BigButton icon={FileSpreadsheet} label="Bulk Excel" tamil="Excel File Upload" onClick={() => setOpenKey("bulk")} testId="btn-bulk-add"/>
            <BigButton icon={Camera} label="Photo (OCR)" tamil="Photo Eduthu Add" onClick={() => setOpenKey("ocr")} testId="btn-ocr-add"/>
            <BigButton icon={Contact2} label="Contacts" tamil="Phone Contacts la irunthu" onClick={() => setOpenKey("contacts")} testId="btn-contacts-add"/>
          </div>
        </DialogContent>
      </Dialog>

      <ManualDialog open={openKey === "manual"} onClose={closeMain} defaultSource={defaultSource} isCustomer={isCustomer} onDone={loadWa}/>
      <BulkDialog open={openKey === "bulk"} onClose={closeMain} defaultSource={defaultSource}/>
      <OCRDialog open={openKey === "ocr"} onClose={closeMain} defaultSource={defaultSource}/>
      <ContactsDialog open={openKey === "contacts"} onClose={closeMain} defaultSource={defaultSource}/>
    </div>
  );
}

function BigButton({ icon: Icon, label, tamil, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="group bg-slate-950/60 hover:bg-amber-500/10 border border-slate-800 hover:border-amber-500/50 rounded-2xl p-4 md:p-5 text-left transition-all active:scale-95">
      <Icon className="w-6 h-6 md:w-7 md:h-7 text-amber-500 mb-2"/>
      <div className="font-heading text-sm md:text-base text-white">{label}</div>
      <div className="text-[10px] md:text-xs text-slate-500 mt-0.5">{tamil}</div>
    </button>
  );
}

/* ----------------------------- Manual Dialog ----------------------------- */
function ManualDialog({ open, onClose, defaultSource, isCustomer, onDone }) {
  const [f, setF] = useState({ phone: "", name: "", district: "", taluk: "", loan_type: "", amount: "", notes: "", source_type: isCustomer ? "Customer Referral" : "Direct", source_name: defaultSource });
  const [saving, setSaving] = useState(false);
  const [waAfter, setWaAfter] = useState(!isCustomer);
  const [loans, setLoans] = useState([]);
  useEffect(() => { if (open) setF({ phone: "", name: "", district: "", taluk: "", loan_type: "", amount: "", notes: "", source_type: isCustomer ? "Customer Referral" : "Direct", source_name: defaultSource }); }, [open, defaultSource, isCustomer]);
  useEffect(() => {
    // Loan products from Admin → Loans master (single source of truth).
    api.get("/loans").then(r => setLoans(r.data || [])).catch(() => setLoans([]));
  }, []);
  const phoneNorm = normalisePhone(f.phone);
  const canSave = validPhone(phoneNorm) && !saving;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/leads/universal-add", {
        phone: phoneNorm,
        name: f.name.trim(),
        district: f.district.trim(),
        taluk: f.taluk.trim(),
        loan_type: f.loan_type.trim(),
        amount: f.amount ? Number(f.amount) : 0,
        source_type: f.source_type,
        source_name: f.source_name,
        notes: f.notes,
      });
      toast.success(data.number_only ? "Number saved! (Number Only)" : "Lead added successfully");
      if (waAfter && !isCustomer) {
        try {
          const r = await api.post(`/whatsapp/log/${data.lead_id}`);
          const msg = `Hi ${f.name || 'there'}, Shakthi Consultancy la irunthu ${defaultSource.split(' - ')[1] || 'our team'}. Loan venuma? Reply pannunga.`;
          window.open(`https://wa.me/91${phoneNorm}?text=${encodeURIComponent(msg)}`, "_blank");
          toast.success(`WhatsApp sent · ${r.data.remaining} remaining today`);
        } catch (e) {
          toast.error(errorMessage(e));
        }
      }
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(errorMessage(e)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-dialog">
        <DialogHeader><DialogTitle>{isCustomer ? "Friend Number Add Pannu" : "Manual Lead Add"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-amber-400 text-xs uppercase tracking-widest">Phone Number <span className="text-red-400">*</span></Label>
            <Input data-testid="mnl-phone" placeholder="10 Digit Number Mattum Pothum Bangu!" value={f.phone} maxLength={10}
              onChange={e => setF({ ...f, phone: e.target.value.replace(/\D/g,'') })}
              className="bg-slate-950 border-slate-700 mt-2 text-lg font-mono-alt tracking-wider"/>
            {f.phone && !validPhone(phoneNorm) && <div className="text-xs text-red-400 mt-1">Invalid — must start with 6/7/8/9 and be 10 digits</div>}
          </div>
          <div className="text-[11px] text-slate-500 -mt-2">Only phone required. All other fields optional — save as "Number Only" lead.</div>

          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="mnl-name" placeholder="Name (optional)" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="bg-slate-950 border-slate-700"/>
            <Input data-testid="mnl-district" placeholder="Place (optional)" value={f.district} onChange={e => setF({ ...f, district: e.target.value })} className="bg-slate-950 border-slate-700"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={f.loan_type} onValueChange={(v) => setF({ ...f, loan_type: v })}>
              <SelectTrigger data-testid="mnl-loan" className="bg-slate-950 border-slate-700"><SelectValue placeholder="Loan Type (optional)"/></SelectTrigger>
              <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                {loans.length === 0
                  ? <div className="p-3 text-sm text-slate-400">No loans configured. Contact admin.</div>
                  : loans.map(l => <SelectItem key={l.id || l.name} value={l.name}><span className="inline-flex items-center gap-2"><LoanIcon loan={l} className="w-4 h-4 text-amber-500"/>{l.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <div>
              <Input
                data-testid="mnl-amount"
                placeholder="Amount (optional)"
                type="number"
                value={f.amount}
                onChange={e => setF({ ...f, amount: e.target.value.replace(/\D/g,'') })}
                onBlur={()=>{ const c = clampAmount(f.amount, findLoan(loans, f.loan_type)); if (c.changed) { setF({...f, amount: c.value}); toast.message(c.why); } }}
                className="bg-slate-950 border-slate-700"/>
              {f.loan_type && loanRangeLabel(findLoan(loans, f.loan_type)) && (
                <div className="mt-1 text-[11px] text-amber-300/80 font-mono-alt" data-testid="mnl-amount-range">{loanRangeLabel(findLoan(loans, f.loan_type))}</div>
              )}
            </div>
          </div>
          <Textarea data-testid="mnl-notes" placeholder="Notes (optional)" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="bg-slate-950 border-slate-700"/>

          <div className="pt-3 border-t border-slate-800">
            <Label className="text-slate-400 text-xs uppercase tracking-widest">Source Type</Label>
            <Select value={f.source_type} onValueChange={v => setF({ ...f, source_type: v })}>
              <SelectTrigger data-testid="mnl-src-type" className="bg-slate-950 border-slate-700 mt-2"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64">
                {SOURCE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="mnl-src-name" placeholder="Source name (auto)" value={f.source_name} onChange={e => setF({ ...f, source_name: e.target.value })} className="bg-slate-950 border-slate-700 mt-2"/>
          </div>

          {!isCustomer && (
            <label className="flex items-center gap-2 text-sm text-slate-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <input type="checkbox" checked={waAfter} onChange={e => setWaAfter(e.target.checked)} data-testid="mnl-wa-toggle"/>
              <MessageCircle className="w-4 h-4 text-emerald-400"/>
              Send WhatsApp intro message (counts toward 100/day)
            </label>
          )}
          <Button data-testid="mnl-save" onClick={save} disabled={!canSave} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
            {saving ? "Saving..." : "Save Number"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ Bulk Dialog ------------------------------ */
function BulkDialog({ open, onClose, defaultSource }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [srcName] = useState(defaultSource);
  useEffect(() => { if (open) { setRows([]); setResult(null); } }, [open]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      await loadScript(SHEETJS_URL);
      const XLSX = window.XLSX;
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const parsed = raw.map((r, i) => {
        const lc = Object.fromEntries(Object.entries(r).map(([k, v]) => [String(k).trim().toLowerCase().replace(/\s+/g,"_"), v]));
        const p = normalisePhone(lc.phone || lc.mobile || lc.number || "");
        return {
          row: i + 2,
          phone: p,
          name: String(lc.name || "").trim(),
          district: String(lc.district || lc.place || "").trim(),
          taluk: String(lc.taluk || "").trim(),
          loan_type: String(lc.loan_type || lc.loan || "").trim(),
          amount: Number(String(lc.amount || 0).replace(/[^\d.]/g,"")) || 0,
          valid: validPhone(p),
        };
      });
      setRows(parsed);
      toast.success(`Parsed ${parsed.length} rows`);
    } catch (err) { toast.error("Parse failed: " + err.message); }
    setBusy(false);
  };

  const upload = async () => {
    const valid = rows.filter(r => r.valid);
    if (valid.length === 0) return toast.error("No valid phone numbers");
    setBusy(true);
    let created = 0, failed = 0;
    for (let i = 0; i < valid.length; i += 50) {
      const chunk = valid.slice(i, i + 50);
      for (const r of chunk) {
        try {
          await api.post("/leads/universal-add", {
            phone: r.phone, name: r.name, district: r.district, taluk: r.taluk,
            loan_type: r.loan_type, amount: r.amount,
            source_type: "Bulk Upload", source_name: srcName, notes: "",
          });
          created++;
        } catch { failed++; }
      }
    }
    setResult({ created, failed });
    toast.success(`${created} imported · ${failed} failed`);
    setBusy(false);
  };

  const downloadSample = async () => {
    await loadScript(SHEETJS_URL);
    const XLSX = window.XLSX;
    const ws = XLSX.utils.json_to_sheet([
      { name: "Ravi", phone: "9876543210", district: "Chennai", taluk: "Egmore", loan_type: "Home Loan", amount: 2500000 },
      { name: "", phone: "9800000001", district: "", taluk: "", loan_type: "", amount: 0 },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, "shakthi_leads_sample.xlsx");
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-dialog">
        <DialogHeader><DialogTitle>Bulk Excel Upload</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 justify-between bg-slate-950/50 border border-slate-800 rounded-lg p-3">
            <input data-testid="bulk-file" type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="text-sm text-slate-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-amber-500 file:text-slate-900 file:font-bold"/>
            <Button variant="secondary" onClick={downloadSample} data-testid="bulk-sample" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full text-xs">Download Sample</Button>
          </div>
          <div className="text-xs text-slate-500">Only phone required. Missing name/place/loan/amount are OK — saved as "Number Only".</div>

          {rows.length > 0 && (
            <>
              <div className="text-sm">
                <span className="text-emerald-400">{rows.filter(r=>r.valid).length} valid</span> · <span className="text-red-400">{rows.filter(r=>!r.valid).length} invalid</span> · Showing first 10:
              </div>
              <div className="max-h-72 overflow-y-auto border border-slate-800 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-950 sticky top-0"><tr className="text-slate-400">
                    <th className="p-2 text-left">Row</th><th className="p-2 text-left">Phone</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Place</th><th className="p-2 text-left">Loan</th><th className="p-2 text-left">Amount</th>
                  </tr></thead>
                  <tbody>
                    {rows.slice(0, 10).map((r, i) => (
                      <tr key={i} className={`border-t border-slate-800 ${r.valid ? "" : "bg-red-500/5"}`} data-testid={`bulk-row-${r.row}`}>
                        <td className="p-2 text-slate-500 font-mono-alt">{r.row}</td>
                        <td className="p-2 font-mono-alt">{r.phone || <span className="text-red-400">missing</span>}</td>
                        <td className="p-2">{r.name || <span className="text-slate-600">—</span>}</td>
                        <td className="p-2">{r.district || <span className="text-slate-600">—</span>}</td>
                        <td className="p-2">{r.loan_type || <span className="text-slate-600">—</span>}</td>
                        <td className="p-2">₹{Number(r.amount||0).toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button onClick={upload} disabled={busy || rows.filter(r=>r.valid).length===0} data-testid="bulk-upload" className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                {busy ? "Uploading..." : `Upload ${rows.filter(r=>r.valid).length} Leads`}
              </Button>
            </>
          )}
          {result && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid="bulk-result">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 inline mr-2"/>
              {result.created} imported · {result.failed} failed
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------- OCR Dialog ------------------------------- */
function OCRDialog({ open, onClose, defaultSource }) {
  const [image, setImage] = useState(null);   // data URL
  const [imageMime, setImageMime] = useState("image/jpeg");
  const [phones, setPhones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [listName, setListName] = useState("");
  useEffect(() => { if (open) { setImage(null); setPhones([]); setOcrProgress(0); setResult(null); setListName(""); } }, [open]);

  const fileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageMime(file.type || "image/jpeg");
    const reader = new FileReader();
    reader.onload = () => { setImage(reader.result); runOCR(reader.result); };
    reader.readAsDataURL(file);
  };
  const runOCR = async (dataUrl) => {
    setBusy(true); setPhones([]); setOcrProgress(0);
    try {
      await loadScript(TESSERACT_URL);
      const T = window.Tesseract;
      const { data } = await T.recognize(dataUrl, "eng", { logger: m => m.progress && setOcrProgress(Math.round(m.progress * 100)) });
      const text = data.text || "";
      const found = Array.from(new Set((text.match(/[6-9]\d{9}/g) || []).map(normalisePhone).filter(validPhone)));
      setPhones(found);
      toast.success(`OCR found ${found.length} phone numbers`);
    } catch (err) {
      toast.error("OCR failed: " + err.message);
    }
    setBusy(false);
  };
  const upload = async () => {
    if (!listName.trim()) return toast.error("Please enter a Source List Name");
    if (phones.length === 0) return toast.error("No phones to add");
    setBusy(true);
    try {
      const b64 = image ? image.split(",")[1] : "";
      const { data } = await api.post("/pool/ocr", {
        source_list_name: listName.trim(),
        phones, image_base64: b64, image_mime: imageMime,
      });
      setResult(data);
      toast.success(`${data.created} added to Source Pool`);
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ocr-dialog">
        <DialogHeader><DialogTitle>📷 Photo Eduthu Add Pannu (OCR) → Source Pool</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-amber-400 text-xs uppercase tracking-widest">Source List Name <span className="text-red-400">*</span></Label>
            <Input data-testid="ocr-list-name" placeholder="Enter Source List Name" value={listName} onChange={e => setListName(e.target.value)} className="bg-slate-950 border-slate-700 mt-2"/>
          </div>
          <div className="border border-dashed border-slate-700 rounded-lg p-4 bg-slate-950/40 text-center">
            <Camera className="w-8 h-8 text-slate-500 mx-auto mb-2"/>
            <input data-testid="ocr-file" type="file" accept="image/*" capture="environment" onChange={fileChange} className="text-sm text-slate-400 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-amber-500 file:text-slate-900 file:font-bold"/>
            <div className="text-[10px] text-slate-500 mt-2">Camera or Gallery — Tamil + English text supported</div>
          </div>
          {image && <img src={image} alt="preview" className="max-h-64 mx-auto rounded-lg border border-slate-800"/>}
          {busy && <div className="text-xs text-slate-400">OCR running... {ocrProgress}%</div>}
          {phones.length > 0 && (
            <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-3">
              <div className="text-xs uppercase text-amber-400 tracking-widest mb-2">Phones found: {phones.length}</div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {phones.map((p, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`ocr-phone-${i}`}>
                    <Input value={p} onChange={e => { const v = normalisePhone(e.target.value); const cp = [...phones]; cp[i] = v; setPhones(cp); }} className="bg-slate-900 border-slate-700 font-mono-alt"/>
                    <button onClick={() => setPhones(phones.filter((_, j) => j !== i))} className="text-red-400"><Trash2 className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
              <Button onClick={upload} disabled={busy} data-testid="ocr-save" className="w-full mt-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                {busy ? "Saving..." : `Save ${phones.filter(validPhone).length} Numbers`}
              </Button>
            </div>
          )}
          {result && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid="ocr-result">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 inline mr-2"/>
              {result.created} added · {result.skipped} skipped
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------- Contacts Dialog ---------------------------- */
function ContactsDialog({ open, onClose, defaultSource }) {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [result, setResult] = useState(null);
  const [listName, setListName] = useState("");
  useEffect(() => { if (open) { setContacts([]); setSelected({}); setSearch(""); setResult(null); setListName(""); } }, [open]);

  const pickContacts = async () => {
    if (!("contacts" in navigator && "select" in navigator.contacts)) {
      toast.error("Not supported on this device. Upload a VCF file instead.");
      return;
    }
    try {
      const props = ["name", "tel"];
      const opts = { multiple: true };
      const list = await navigator.contacts.select(props, opts);
      const mapped = [];
      list.forEach(c => {
        const nm = (c.name && c.name[0]) || "";
        (c.tel || []).forEach(t => {
          const p = normalisePhone(t);
          if (validPhone(p)) mapped.push({ name: nm, phone: p });
        });
      });
      // dedup by phone
      const seen = new Set();
      const uniq = mapped.filter(x => (seen.has(x.phone) ? false : (seen.add(x.phone), true)));
      setContacts(uniq);
      setSelected(Object.fromEntries(uniq.map(c => [c.phone, true])));
      toast.success(`Loaded ${uniq.length} contacts`);
    } catch (e) { toast.error("Failed: " + e.message); }
  };

  const vcfChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const cards = text.split(/BEGIN:VCARD/i).slice(1);
    const list = [];
    for (const card of cards) {
      const nameM = card.match(/FN[^:]*:([^\r\n]+)/i);
      const nm = nameM ? nameM[1].trim() : "";
      const tels = [...card.matchAll(/TEL[^:]*:([^\r\n]+)/gi)].map(m => normalisePhone(m[1])).filter(validPhone);
      tels.forEach(p => list.push({ name: nm, phone: p }));
    }
    const seen = new Set();
    const uniq = list.filter(x => (seen.has(x.phone) ? false : (seen.add(x.phone), true)));
    setContacts(uniq);
    setSelected(Object.fromEntries(uniq.map(c => [c.phone, true])));
    toast.success(`Parsed ${uniq.length} contacts from VCF`);
  };

  const filtered = contacts.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search));
  const selectedList = contacts.filter(c => selected[c.phone]);

  const importAll = async () => {
    if (!listName.trim()) return toast.error("Please enter a Source List Name");
    if (selectedList.length === 0) return toast.error("Select at least one contact");
    setBusy(true);
    try {
      const { data } = await api.post("/pool/contacts", {
        source_list_name: listName.trim(),
        contacts: selectedList,
      });
      setResult(data);
      toast.success(`${data.created} added to Source Pool`);
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contacts-dialog">
        <DialogHeader><DialogTitle>📱 Phone Contacts la irunthu Add Pannu → Source Pool</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-amber-400 text-xs uppercase tracking-widest">Source List Name <span className="text-red-400">*</span></Label>
            <Input data-testid="contacts-list-name" placeholder="Enter Source List Name" value={listName} onChange={e => setListName(e.target.value)} className="bg-slate-950 border-slate-700 mt-2"/>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={pickContacts} data-testid="contacts-pick" className="bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full">
              <Contact2 className="w-4 h-4 mr-2"/>Pick from Phone
            </Button>
            <label className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-full text-sm cursor-pointer">
              or Upload VCF
              <input type="file" accept=".vcf" onChange={vcfChange} className="hidden" data-testid="contacts-vcf"/>
            </label>
          </div>
          {contacts.length > 0 && (
            <>
              <div className="flex gap-2">
                <Input placeholder="Search name / phone..." value={search} onChange={e => setSearch(e.target.value)} className="bg-slate-950 border-slate-700 flex-1"/>
                <Button variant="secondary" onClick={() => setSelected(Object.fromEntries(contacts.map(c => [c.phone, true])))} className="bg-slate-800 hover:bg-slate-700 text-white rounded-full text-xs">Select All</Button>
                <Button variant="secondary" onClick={() => setSelected({})} className="bg-slate-800 hover:bg-slate-700 text-white rounded-full text-xs">Clear</Button>
              </div>
              <div className="max-h-72 overflow-y-auto border border-slate-800 rounded-lg">
                {filtered.map((c, i) => (
                  <label key={c.phone} className="flex items-center gap-3 p-3 border-b border-slate-800 last:border-0 hover:bg-slate-800/50 cursor-pointer" data-testid={`contact-${c.phone}`}>
                    <input type="checkbox" checked={!!selected[c.phone]} onChange={e => setSelected({ ...selected, [c.phone]: e.target.checked })}/>
                    <div className="flex-1">
                      <div className="text-white text-sm">{c.name || <span className="text-slate-500 italic">No name</span>}</div>
                      <div className="text-xs text-slate-500 font-mono-alt">{c.phone}</div>
                    </div>
                  </label>
                ))}
              </div>
              <Button onClick={importAll} disabled={busy || !listName.trim() || selectedList.length===0} data-testid="contacts-import-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                {busy ? "Importing..." : `Add ${selectedList.length} → Source Pool`}
              </Button>
            </>
          )}
          {result && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid="contacts-result">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 inline mr-2"/>
              {result.created} imported · {result.skipped} skipped
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
